// CoWIN vaccination certificate parser.
// Source: PDF download from https://www.cowin.gov.in/ after sign-in. The
// PDF has a verifiable QR code (W3C VC + JWS) and printed dose details.
//
// In test mode we emit a realistic two-dose mock. The integrating app pipes
// the printed dose section through Adobe PDF MCP extract_pdf and routes the
// text here. The QR-code verification path is sketched but not yet bundled
// (requires a JWS verifier with the CoWIN public key set, which the CCEW
// publishes at /api/v2/registration/certificate/public).

import type { ConnectorAdapter, ConnectorMode, ConnectorResult, ConnectorStatus } from './_types.ts';
import { err, nowIso, ok, shortHash } from './_types.ts';

export interface CowinDose {
  readonly doseNumber: 1 | 2 | 3 | 4;
  readonly vaccineName: string;
  readonly batchNumber: string;
  readonly dateOfDose: string;
  readonly siteName: string;
  readonly vaccinatedBy: string;
  readonly stateName: string;
}

export interface CowinCertificate {
  readonly beneficiaryName: string;
  readonly beneficiaryRefId: string;
  readonly age: number;
  readonly gender: 'Male' | 'Female' | 'Other';
  readonly photoIdRedacted: string;
  readonly doses: readonly CowinDose[];
  readonly qrVerified: boolean | null;
  readonly issuedAt: string;
}

export interface CowinConnectorStatus extends ConnectorStatus {
  readonly id: 'cowin-vax-cert';
  readonly doseCount: number;
}

interface CowinPersist {
  readonly connectedAt: string;
  readonly refIdHash: string;
  readonly doseCount: number;
}

const STORE_KEY = 'empire/connectors/cowin-vax-cert';

export class CowinVaxCertAdapter
  implements ConnectorAdapter<CowinConnectorStatus, CowinCertificate>
{
  readonly id = 'cowin-vax-cert';
  readonly displayName = 'CoWIN vaccination certificate';
  readonly displayNameHi = 'कोविन टीकाकरण प्रमाण-पत्र';
  readonly dpdpCategory = 'health-vaccination-record';
  readonly mode: ConnectorMode;
  #pendingText: string | null = null;

  constructor(mode: ConnectorMode = 'test') {
    this.mode = mode;
  }

  async getStatus(): Promise<CowinConnectorStatus> {
    const persisted = readPersist();
    return {
      id: this.id,
      mode: this.mode,
      connected: persisted !== null,
      lastSyncAt: persisted?.connectedAt ?? null,
      principalIdHash: persisted?.refIdHash ?? null,
      doseCount: persisted?.doseCount ?? 0,
      notes: 'Parses CoWIN cert text. QR-JWS verification deferred to v1.1.',
    };
  }

  async connect(): Promise<ConnectorResult<CowinConnectorStatus>> {
    return ok(await this.getStatus());
  }

  async disconnect(): Promise<void> {
    if (typeof localStorage !== 'undefined') localStorage.removeItem(STORE_KEY);
  }

  queueText(text: string): void {
    this.#pendingText = text;
  }

  async importPayload(): Promise<ConnectorResult<CowinCertificate>> {
    if (!this.#pendingText) {
      const mock = this.mockPayload();
      await this.#persist(mock);
      return ok(mock);
    }
    const cert = parseCowinText(this.#pendingText);
    if (!cert) {
      return err({ code: 'parse', message: 'CoWIN certificate parser found no doses.', retriable: false });
    }
    await this.#persist(cert);
    return ok(cert);
  }

  mockPayload(): CowinCertificate {
    return {
      beneficiaryName: 'Aditya Sharma',
      beneficiaryRefId: '12-3456-7890-1234',
      age: 33,
      gender: 'Male',
      photoIdRedacted: 'Aadhaar # XXXX XXXX 4321',
      doses: [
        { doseNumber: 1, vaccineName: 'COVISHIELD', batchNumber: '4121Z045', dateOfDose: '2021-05-08', siteName: 'Holy Family Hospital, Okhla', vaccinatedBy: 'Sister Mary', stateName: 'Delhi' },
        { doseNumber: 2, vaccineName: 'COVISHIELD', batchNumber: '4121Z110', dateOfDose: '2021-08-12', siteName: 'Holy Family Hospital, Okhla', vaccinatedBy: 'Sister Mary', stateName: 'Delhi' },
        { doseNumber: 3, vaccineName: 'CORBEVAX', batchNumber: '5022B208', dateOfDose: '2022-08-10', siteName: 'New Friends Colony CHC', vaccinatedBy: 'Dr Singh', stateName: 'Delhi' },
      ],
      qrVerified: null,
      issuedAt: nowIso(),
    };
  }

  async #persist(c: CowinCertificate): Promise<void> {
    if (typeof localStorage === 'undefined') return;
    const rec: CowinPersist = {
      connectedAt: nowIso(),
      refIdHash: await shortHash(c.beneficiaryRefId),
      doseCount: c.doses.length,
    };
    localStorage.setItem(STORE_KEY, JSON.stringify(rec));
  }
}

function readPersist(): CowinPersist | null {
  if (typeof localStorage === 'undefined') return null;
  const raw = localStorage.getItem(STORE_KEY);
  if (!raw) return null;
  try { return JSON.parse(raw) as CowinPersist; } catch { return null; }
}

const DOSE_RE = /Dose\s*(\d)\s*[\s\S]{0,400}?Vaccine\s*Name\s*[:\-]?\s*([A-Z0-9\- ]+)[\s\S]{0,400}?Batch\s*Number\s*[:\-]?\s*([A-Z0-9]+)[\s\S]{0,400}?Date\s*of\s*Dose\s*[:\-]?\s*([\d\-\/\.]+)[\s\S]{0,400}?Site\s*Name\s*[:\-]?\s*([^\n]+)[\s\S]{0,400}?Vaccinated\s*by\s*[:\-]?\s*([^\n]+)[\s\S]{0,400}?State\s*[:\-]?\s*([^\n]+)/g;

export function parseCowinText(text: string): CowinCertificate | null {
  const beneficiaryName = grab(text, /Beneficiary\s*Name\s*[:\-]?\s*([A-Z][A-Za-z\s\.]+)/);
  const ref = grab(text, /Beneficiary\s*Reference\s*Id\s*[:\-]?\s*([\d\-]+)/i);
  const ageRaw = grab(text, /Age\s*[:\-]?\s*(\d{1,3})/);
  const genderRaw = grab(text, /Gender\s*[:\-]?\s*(Male|Female|Other)/i);
  const doses: CowinDose[] = [];
  let m: RegExpExecArray | null;
  while ((m = DOSE_RE.exec(text)) !== null) {
    const n = parseInt(m[1] ?? '0', 10);
    if (n < 1 || n > 4) continue;
    doses.push({
      doseNumber: n as 1 | 2 | 3 | 4,
      vaccineName: (m[2] ?? '').trim(),
      batchNumber: (m[3] ?? '').trim(),
      dateOfDose: normaliseDoseDate(m[4] ?? ''),
      siteName: (m[5] ?? '').trim(),
      vaccinatedBy: (m[6] ?? '').trim(),
      stateName: (m[7] ?? '').trim(),
    });
  }
  if (doses.length === 0) return null;
  const genderNormalised: CowinCertificate['gender'] =
    /^male/i.test(genderRaw) ? 'Male' :
    /^female/i.test(genderRaw) ? 'Female' : 'Other';
  return {
    beneficiaryName: beneficiaryName.trim(),
    beneficiaryRefId: ref || 'unknown',
    age: ageRaw ? parseInt(ageRaw, 10) : 0,
    gender: genderNormalised,
    photoIdRedacted: '',
    doses,
    qrVerified: null,
    issuedAt: nowIso(),
  };
}

function grab(text: string, re: RegExp): string {
  const m = re.exec(text);
  return m && m[1] ? m[1].trim() : '';
}

function normaliseDoseDate(input: string): string {
  if (!input) return '';
  const m = /^(\d{1,2})[\/\.\-](\d{1,2})[\/\.\-](\d{2,4})$/.exec(input);
  if (m) {
    const dd = (m[1] ?? '').padStart(2, '0');
    const mm = (m[2] ?? '').padStart(2, '0');
    let yyyy = m[3] ?? '';
    if (yyyy.length === 2) yyyy = `20${yyyy}`;
    return `${yyyy}-${mm}-${dd}`;
  }
  return input;
}

export function makeCowinVaxCert(mode: ConnectorMode): CowinVaxCertAdapter {
  return new CowinVaxCertAdapter(mode);
}
